import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { aiFeatures, ragFeatures } from '../data/mock';
import { Target, FileSearch, Search, Workflow, Sparkles, Layers, FileText, Building, ListOrdered } from 'lucide-react';

const iconMap = { Target, FileSearch, Search, Workflow };
const ragIcons = [Layers, FileText, Building, ListOrdered];

const AIMatchingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-neutral-100 border border-neutral-200 rounded-full px-4 py-2 mb-8">
              <Sparkles className="w-4 h-4 text-neutral-600" />
              <span className="text-neutral-600 text-sm font-medium">Neural-Powered</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-black leading-[0.95] mb-6 tracking-tight">
              Sandgen
              <br />
              <span className="text-neutral-400">Intelligence.</span>
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              AI that doesn&apos;t just search—it understands. While others match keywords, 
              we match potential. The difference is everything.
            </p>
          </div>
        </div>
      </section>

      {/* AI Features */}
      <section className="section bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="font-display text-3xl md:text-4xl text-black mb-4">The Neural Advantage</h2>
          <p className="text-neutral-500 mb-12 max-w-2xl">Four pillars of intelligence that make traditional ATS look prehistoric.</p>
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {aiFeatures.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <div key={index} className="bg-white border border-neutral-200 p-10 group hover:border-neutral-300 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 bg-neutral-100 border border-neutral-200 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-black group-hover:border-black group-hover:text-white transition-all duration-300">
                      <Icon className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-black text-xl font-semibold mb-3">{feature.title}</h3>
                      <p className="text-neutral-500 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* How It Works */}
          <div className="bg-white border border-neutral-200 p-10 lg:p-12 rounded-xl">
            <h3 className="text-black text-2xl font-semibold mb-4 text-center">The Neural Sequence</h3>
            <p className="text-neutral-500 text-center mb-10 max-w-2xl mx-auto">Four steps. Milliseconds. Magic.</p>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Decode', desc: 'We dissect your JD like surgeons. Every requirement, captured.' },
                { step: '2', title: 'Parse', desc: 'Résumés transformed into structured intelligence.' },
                { step: '3', title: 'Comprehend', desc: 'Semantic matching that understands meaning, not just words.' },
                { step: '4', title: 'Rank', desc: 'Confidence scores that tell you exactly who to call first.' }
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl font-bold">{item.step}</span>
                  </div>
                  <h4 className="text-black font-semibold mb-2">{item.title}</h4>
                  <p className="text-neutral-500 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RAG Matching */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Match</span>
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
              RAG-Powered. <span className="text-neutral-400">Patent-Pending.</span>
            </h2>
            <p className="text-lg text-neutral-500">
              Retrieval-Augmented Generation isn&apos;t just a buzzword here. It&apos;s the core 
              of how we find candidates that others simply can&apos;t.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {ragFeatures.map((feature, index) => {
              const Icon = ragIcons[index];
              return (
                <div key={index} className="bg-neutral-50 border border-neutral-100 p-8 group hover:bg-white hover:border-neutral-200 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 bg-white border border-neutral-200 rounded-xl flex items-center justify-center mb-6 group-hover:bg-black group-hover:border-black group-hover:text-white transition-all duration-300">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-black font-semibold mb-3">{feature.title}</h3>
                  <p className="text-neutral-500 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>

          {/* Visual Demo */}
          <div className="bg-neutral-50 border border-neutral-100 p-10 lg:p-12 rounded-xl">
            <h3 className="text-black text-xl font-semibold mb-4">Watch the Magic</h3>
            <p className="text-neutral-500 mb-10">From job description to ranked candidates in under a second.</p>
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div className="bg-white border border-neutral-200 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-neutral-600" />
                  <span className="text-black font-medium">Input</span>
                </div>
                <div className="text-neutral-500 text-sm">
                  <p>&ldquo;Looking for a <span className="text-black font-medium">senior backend engineer</span> with <span className="text-black font-medium">distributed systems</span> expertise...&rdquo;</p>
                </div>
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <span className="text-neutral-400 text-xs uppercase tracking-widest">Extracted</span>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="px-2 py-0.5 bg-black text-white text-xs rounded">Backend</span>
                    <span className="px-2 py-0.5 bg-black text-white text-xs rounded">Senior</span>
                    <span className="px-2 py-0.5 bg-black text-white text-xs rounded">Distributed</span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col justify-center items-center">
                <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mb-4">
                  <Layers className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-black font-medium mb-2">Sandgen Intelligence</h4>
                <p className="text-neutral-500 text-sm text-center">Neural processing in 0.3 seconds</p>
              </div>
              
              <div className="bg-white border border-neutral-200 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <ListOrdered className="w-5 h-5 text-neutral-600" />
                  <span className="text-black font-medium">Output</span>
                </div>
                <div className="space-y-3">
                  {[
                    { name: 'Alex Turner', score: 96 },
                    { name: 'Maya Patel', score: 92 },
                    { name: 'Chris Wong', score: 88 }
                  ].map((candidate, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-black rounded text-white text-xs flex items-center justify-center font-medium">{i + 1}</span>
                      <span className="text-neutral-700 text-sm flex-1">{candidate.name}</span>
                      <span className="text-black font-semibold">{candidate.score}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIMatchingPage;
